import { useSyncExternalStore,
  memo,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  LayoutList,
  LineChart,
  Maximize2,
  PanelLeftOpen,
  Wallet,
} from 'lucide-react'
import {
  isSelfTradeCancel,
  RMS_GATE_REASON,
  SELF_TRADE_HELP,
} from '@apna/shared'
import { api } from '@/lib/api'
import { useBookUpdates } from '@/hooks/useBookUpdates'
import { tickSocket, type BookUpdate } from '@/feed/tickSocket'
import { useExitOrder } from './useExitOrder'
import { PayoffDock } from './PayoffDock'
import { cn } from '@/lib/utils'
import { useTickSubscription } from '@/feed/useTick'
import { useDayTotals, type DayState } from './useDayTotals'
import { useOrders } from '@/store/useOrders'
import { isWorking, orderStore, type TrackedIntent } from '@/store/orders'
import { ORDER_PAGE, ordersToday } from './ordersToday'
import { qtyOf, qtyTitle } from './orderQty'
import { compactInr } from './quantity'
import { Notice } from '@/components/Notice'
import { ManualLegs, TradeCard, type LegDragApi } from './TradeCards'
import { useLegGrouping } from './useLegGrouping'
import { restingExitsFor, restingCountLabel, type RestingExit } from './restingExits'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { NameGroupDialog } from './NameGroupDialog'
import { OpenRiskChip } from './OpenRiskChip'
import { useOpenRisk, type BookRisk } from './useOpenRisk'

/**
 * The `which` an exit-all of the manual block is tracked under — ONE PER
 * BROKER ACCOUNT.
 *
 * `useExitOrder` keys its in-flight marker on one string, and every other
 * caller passes a trade or leg id. This block is neither: it is N trades,
 * across however many accounts they were clicked on. An order can only be
 * addressed to one connection, so one press of EXIT ALL becomes one exit per
 * account — and each needs its own marker, or the first to settle would clear
 * the card state of rows belonging to a broker still working.
 *
 * `busyFor` below reads it as "every manual trade ON THAT ACCOUNT is busy".
 */
const manualExitKey = (connectionId: string): string =>
  `manual-all:${connectionId}`

/**
 * A provisional row, in the shape the book already draws — ROADMAP 9.26.
 *
 * A SYNTHETIC `OpenBookTrade` rather than a parallel type, for the reason
 * 9.23 gives about the journal: the drawer already splits, counts, sizes
 * and draws a trade, and a second shape would have to teach every one of
 * those about itself.
 *
 * `netQty: 0` is the load-bearing field. It is literally true — nothing is
 * held until a fill — and it is what keeps a provisional row out of OPEN
 * RISK, out of DAY MTM, out of EXIT ALL and out of the held-leg count,
 * while still drawing the contract, the side and the ordered quantity the
 * trader just asked for. `signedQty` already falls back to `qty` with the
 * side's sign for a leg holding nothing, so the row reads correctly with
 * no special case.
 */
function asProvisionalTrade(p: Provisional): OpenBookTrade {
  return {
    tradeId: `provisional:${p.clientRequestId}`,
    status: 'OPENING',
    mode: p.mode,
    underlying: p.underlying,
    label: null,
    strategyId: null,
    strategyName: null,
    openedAt: new Date(p.at).toISOString(),
    autoProtect: false,
    premium: null,
    broker: { connectionId: p.connectionId, label: p.brokerLabel, mode: p.mode },
    legs: p.legs.map((l, seq) => ({
      tradeLegId: `provisional:${p.clientRequestId}:${seq}`,
      seq,
      status: 'PENDING',
      token: l.token,
      side: l.side,
      optionType: l.optionType,
      strike: l.strike,
      expiry: l.expiry,
      symbol: l.symbol,
      lots: l.lotSize > 0 ? Math.round(l.qty / l.lotSize) : 0,
      lotSize: l.lotSize,
      qty: l.qty,
      // NOTHING IS HELD YET, and saying otherwise is the whole hazard.
      netQty: 0,
      avgPrice: 0,
      realisedPnl: 0,
      chargesTotal: 0,
      lastFillAt: null,
      slSpec: null,
      tgtSpec: null,
      orphanedQty: null,
      role: null,
    })),
  } as unknown as OpenBookTrade
}
import { useDragAutoScroll } from './useDragAutoScroll'
import { useLegTransfer } from './useLegTransfer'
import { useLegRole } from './useLegRole'
import { notifyFail, notifyOk } from '@/components/Toast'
import { LegRoleDialog } from './LegRoleDialog'
import type { DraggedLeg } from './legDrop'
import {
  isGrouped,
  splitBook,
  withoutFailedLegs,
  tokensOf,
  useOpenBook,
  asOpenBookTrade,
  tradeName,
  type OpenBookLeg,
  type OpenBookState,
  type OpenBookTrade,
  type SplitBook,
} from './useOpenBook'
import { heldStore } from './heldStore'
import { ExitTicket, type ExitTicketRequest, type ExitTicketTarget } from './ExitTicket'
import { OpenMtm } from './OpenMtm'
import { provisionalStore, type Provisional } from './provisionalStore'

/**
 * The positions / orders drawer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 56px COLLAPSED, 420px OPEN.
 *
 * The chain is the screen; this visits. Collapsed it is a rail of counts
 * wide enough to read at a glance and narrow enough to cost the chain
 * nothing.
 *
 * THE WIDTH ANIMATES, THE CONTENT CROSS-FADES.
 *
 * Both on the design's easing (cubic-bezier(.22,1,.36,1), ~0.5s). The body
 * is absolutely positioned at its full 418px the whole time and only
 * fades/slides, so the text inside never reflows mid-animation — animating
 * a flex child's width would relayout its contents on every frame.
 *
 * REFUSALS SHOW THEIR GATE CODE. That is the panel's reason to exist:
 * "rejected" is not an answer to "why didn't my order go", and FEED_STALE
 * is.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type DrawerTab = 'POSITIONS' | 'ORDERS'

export interface BookDrawerProps {
  open: boolean
  tab: DrawerTab
  onOpen: (tab: DrawerTab) => void
  onClose: () => void
  /*
   * NO `connectionId`. The account selector is for EXECUTION — it chooses
   * where the next order goes. This panel shows every account at once, and
   * filtering it by the selected one was showing a two-broker trader half
   * their risk.
   */
  /** Bumped by the caller to force a re-read. */
  refreshToken: number
  clock: string
  /** True when the book has the workspace to itself and the chain is hidden. */
  chainHidden?: boolean
  /** Put the chain back at the default split. */
  onShowChain?: () => void
  /** Give this panel the whole workspace using the existing split state. */
  onFullScreen?: () => void
  /** The chain's payoff launcher is hidden in full-screen book mode. */
  onPayoffOpen?: () => void
  payoffOpen?: boolean
  onPayoffClose?: () => void
}

export function BookDrawer({
  open,
  tab,
  onOpen,
  onClose,
  chainHidden = false,
  onShowChain,
  onFullScreen,
  onPayoffOpen,
  payoffOpen = false,
  onPayoffClose,
  refreshToken,
  clock,
}: BookDrawerProps) {
  const orders = useOrders()
  /*
   * ── THE BADGE COUNTS WHAT THE TABLE LISTS — 9.31 ────────────────────────
   *
   * It counted INTENTS. One intent carries every leg of a basket, so a
   * thirty-leg strategy read as "1" and a day of 460 order rows read as
   * "200" — the badge and the list under it were counting different
   * things, which is the same failure the positions badge had.
   *
   * One row per LEG, today only, computed here and handed down so the two
   * cannot drift apart.
   */
  const orderRowsToday = useMemo(
    () =>
      ordersToday(
        orders.flatMap((o) =>
          o.legs.map((leg, i) => ({
            key: `${o.clientRequestId}-${i}`,
            at: o.at,
            leg,
            intent: o,
          })),
        ),
        Date.now(),
      ),
    [orders],
  )
  /** Bumped by an exit so the book is re-read without waiting for the caller. */
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), [])

  /*
   * APPLY THE FILL, DO NOT GO AND ASK FOR IT.
   *
   * `BOOK_UPDATED` now carries the changed legs, so a fill updates the rows in
   * place instead of costing a `GET /trades/open` from the user's own
   * connection. The ref exists only because `patchLeg` comes from `useOpenBook`
   * below and this handler is built above it; a notice cannot arrive before the
   * first render completes, and if one somehow did the fallback re-reads, which
   * is the old behaviour and never wrong.
   *
   * It also means a fill landing while someone is typing in a protection panel
   * no longer rebuilds the cards underneath them.
   */
  const bookRef = useRef<{
    patchLeg: (id: string, patch: Partial<OpenBookLeg>) => void
    upsertTrade: (t: OpenBookTrade) => boolean
    removeTrade: (id: string) => boolean
  } | null>(null)
  /**
   * The newest push applied per trade, so a slower fetch cannot undo it.
   *
   * A `GET /trades/open` in flight when a push lands would otherwise answer
   * with the older book and put the card back as it was. The push carries
   * `at`; anything not newer than what has already been applied is ignored.
   */
  const seenAt = useRef(new Map<string, number>())

  const bookRevision = useBookUpdates(
    useCallback((u: BookUpdate): boolean => {
      const book = bookRef.current
      if (!book) return false

      /*
       * STRUCTURAL FIRST. A pushed trade or a close is the complete answer
       * for that trade, so it supersedes any numeric patch in the same
       * notice rather than being applied alongside it.
       */
      /*
       * ── THE REAL ROW REPLACES THE PROVISIONAL ONE — ROADMAP 9.26 ─────
       *
       * Before anything else, because the push that carries `forRequest`
       * IS the confirmation: retiring the provisional in the same handler
       * that inserts the real trade means the two never appear together,
       * even for a frame.
       */
      provisionalStore.confirm(u.forRequest)

      if (u.closed || u.trade !== undefined) {
        if ((seenAt.current.get(u.tradeId) ?? 0) >= u.at) return true
        if (u.closed) {
          seenAt.current.set(u.tradeId, u.at)
          return book.removeTrade(u.tradeId)
        }
        const trade = asOpenBookTrade(u.trade)
        // Unrecognised shape: say so by declining, and let the fetch answer.
        if (!trade) return false
        seenAt.current.set(u.tradeId, u.at)
        return book.upsertTrade(trade)
      }

      if (u.legs.length === 0) return false
      for (const leg of u.legs) {
        book.patchLeg(leg.tradeLegId, {
          netQty: leg.netQty,
          avgPrice: leg.avgPrice,
          realisedPnl: leg.realisedPnl,
          chargesTotal: leg.chargesTotal,
          lastFillAt: leg.lastFillAt,
        })
      }
      return true
    }, []),
  )

  const {
    state: book,
    refresh: refreshBook,
    patchLeg,
    upsertTrade,
    removeTrade,
  } = useOpenBook(refreshToken + refreshNonce + bookRevision)
  bookRef.current = { patchLeg, upsertTrade, removeTrade }

  /*
   * ── THE CHAIN NEEDS THIS READ TOO — ROADMAP 9.11 ────────────────────────
   *
   * With netting on, a sell on a strike already held REDUCES the row instead
   * of adding one, so the chain marks what is held and the outcome is known
   * before the click rather than after it. The chain cannot read the book
   * itself without doubling every `GET /trades/open` — see `heldStore.ts`.
   *
   * PUBLISHED FROM HERE because this is where the read lives, and only on
   * READY: a failed read is not an empty account, and cleared is the only
   * honest answer to "we could not look". A stale badge would mark a strike
   * as reducible against a position nobody has confirmed is still there.
   */
  useEffect(() => {
    if (book.kind === 'READY') heldStore.publish(book.trades)
    else if (book.kind === 'FAILED') heldStore.clear()

    /*
     * ── 9.37. THE HOLDINGS ARE DECLARED TO THE FEED FROM HERE ────────────
     *
     * Same read, same effect, for the same reason the held store is fed
     * here: this is where the open book lives, and doubling
     * `GET /trades/open` to tell the socket about it would be a second
     * answer that can disagree with the first.
     *
     * The engine takes these before anything else when the pool is tight —
     * see `setHoldings`. A chain strike refused is a dash; a held leg
     * refused is a Day MTM that silently understates the book.
     *
     * NOT cleared on a failed read. A read that failed says nothing about
     * what is held, and dropping the priority would hand those slots to
     * chain strikes at exactly the moment we have stopped being able to
     * check. The TTL on the gateway's side is what eventually releases it.
     */
    if (book.kind === 'READY') {
      const tokens: number[] = []
      for (const t of book.trades) {
        for (const l of t.legs) {
          if (l.netQty !== 0 && l.token !== null) tokens.push(l.token)
        }
      }
      tickSocket.setHoldings(tokens)
    }
  }, [book])

  const {
    exit,
    exiting,
    error: exitError,
    notice: exitNotice,
    setError: setExitError,
  } = useExitOrder(book, orders, refresh)

  /*
   * ── MOVING A LEG BETWEEN TRADES — 9.9 ───────────────────────────────────
   *
   * The leg in the hand is held HERE rather than in a card because every
   * drop target needs it at once: HTML5 drag-and-drop keeps `dataTransfer`
   * opaque until the drop, so a `dragover` handler cannot ask what it is
   * being offered and the highlighting has to be driven from state.
   *
   * The move re-reads the book rather than patching it in place. The server
   * decides against its own read and may refuse what this screen offered, so
   * the row stays where it is until the move has actually committed.
   */
  const [dragged, setDragged] = useState<DraggedLeg | null>(null)
  const { moving, refusal, landed, move, dismiss } = useLegTransfer(refresh)
  const { saving: savingRole, setRole } = useLegRole(
    useCallback(
      (tradeLegId: string, role) => patchLeg(tradeLegId, { role }),
      [patchLeg],
    ),
  )

  /*
   * The list scrolls itself while a leg is in the hand. Without it a leg can
   * only reach a basket that was already on screen when the drag began — see
   * the hook for why the browser's own edge scroll does not cover this.
   */
  const dragScrollPort = useDragAutoScroll(dragged !== null)

  const legDrag = useMemo<LegDragApi>(
    () => ({
      dragged,
      moving,
      onDragStart: (trade, leg) => {
        dismiss()
        setDragged({
          tradeLegId: leg.tradeLegId,
          tradeId: trade.tradeId,
          grouped: isGrouped(trade),
          underlying: trade.underlying,
          connectionId: trade.broker.connectionId,
          token: leg.token,
          netQty: leg.netQty,
          symbol: leg.symbol,
        })
      },
      onDragEnd: () => setDragged(null),
      onDrop: (targetTradeId, intoName) => {
        if (dragged)
          void move(dragged.tradeLegId, targetTradeId, intoName, dragged.symbol)
        setDragged(null)
      },
      onRole: (tradeLegId, role) => void setRole(tradeLegId, role),
    }),
    [dragged, moving, move, dismiss, setRole],
  )

  /*
   * ── THE ANSWER IS A TOAST, NOT A BANNER IN THE LIST ──────────────────────
   *
   * Both of these used to render inside the drawer, above the positions, and
   * that made them a layout bug rather than a notice: appearing pushed every
   * card down and auto-dismissing pulled them back up. A reflow under the
   * cursor during an HTML5 drag moves the row being dragged, and the browser
   * ABORTS the drag — so four seconds after any move, the next drag died
   * mid-gesture and the mouse appeared to un-click by itself.
   *
   * `sonner` renders in a fixed overlay that is not in the drawer's flow, so
   * neither appearing nor leaving can move anything. It is also where every
   * other "what just happened" in this app already lives — an order placed
   * from the chain, a strategy fired — which is the second reason to use it.
   *
   * Fired from an effect rather than at the call site so it happens exactly
   * once per outcome: `dismiss` clears the state, the effect re-runs against
   * null, and nothing repeats.
   */
  useEffect(() => {
    if (landed === null || landed.ask) return
    notifyOk(`Leg moved to ${landed.into}`, landed.symbol)
    dismiss()
  }, [landed, dismiss])

  useEffect(() => {
    if (refusal === null) return
    notifyFail('The leg was not moved', refusal)
    dismiss()
  }, [refusal, dismiss])
  useEffect(() => {
    let cancelled = false
    const version = orderStore.getVersion()
    void api
      .get<{ items: TrackedIntent[] }>('/market/orders')
      .then((result) => {
        if (!cancelled && Array.isArray(result.items))
          orderStore.hydrate(result.items, version)
      })
      .catch(() => {
        /* A failed read must not clear the last known orders. */
      })
    return () => {
      cancelled = true
    }
  }, [bookRevision, refreshNonce, refreshToken])
  /*
   * ── PROVISIONAL ROWS JOIN THE BOOK — ROADMAP 9.26 ───────────────────────
   *
   * Painted from the click and merged here, so everything downstream — the
   * split, the badge, the footer, the cards — treats them as the positions
   * they are about to be. The one thing they are NOT counted in is risk:
   * `netQty` is 0 until a fill, so OPEN RISK and DAY MTM read exactly what
   * they read before, which is the point of not inventing a price.
   *
   * Ahead of the real trades, matching the server's own newest-first order
   * for a card that has only just appeared.
   */
  const provisional = useSyncExternalStore(
    provisionalStore.subscribe,
    provisionalStore.getSnapshot,
    provisionalStore.getSnapshot,
  )
  const live = book.kind === 'READY' ? book.trades : EMPTY_TRADES
  /*
   * Stripped HERE, above the split, so the pane, the badge, the footer and
   * the risk headline all see the same book — the same reason the split
   * itself happens once. A leg the broker refused is not a position; see
   * `withoutFailedLegs`.
   */
  const trades = useMemo(
    () =>
      withoutFailedLegs(
        provisional.rows.length === 0
          ? live
          : [...provisional.rows.map(asProvisionalTrade), ...live],
      ),
    [provisional.rows, live],
  )
  /*
   * Split ONCE, here, and pass it down. The badge, the footer and the pane
   * must agree about what counts as a position — computing it twice is how
   * a tab saying 50 ends up above a pane showing none.
   */
  const split = useMemo(() => splitBook(trades), [trades])

  /*
   * ── 9.26. GROUPING LOOSE LEGS ────────────────────────────────────────────
   *
   * The selection lives here rather than in `ManualLegs` because it has to
   * outlive that component's re-renders as the book polls, and because the
   * dialog it opens belongs to the drawer rather than to one block inside
   * it. `useLegGrouping` prunes it against what is actually on screen.
   */
  const grouping = useLegGrouping(split.manual)
  const [naming, setNaming] = useState<
    | { kind: 'GROUP' }
    | { kind: 'RENAME'; tradeId: string; initial: string }
    | null
  >(null)
  const [namingBusy, setNamingBusy] = useState(false)
  const [namingError, setNamingError] = useState<string | null>(null)

  const submitName = useCallback(
    async (label: string) => {
      if (!naming) return
      setNamingBusy(true)
      setNamingError(null)
      try {
        if (naming.kind === 'GROUP') {
          const r = await api.post<{ tradeId: string; refused: { why: string }[] }>(
            '/trades/legs/group',
            { tradeLegIds: [...grouping.selected], label },
          )
          /*
           * A refusal on SOME legs is not a failure of the action: the rest
           * grouped, and the server's own sentence says which could not.
           * Shown and left open so the trader reads it before dismissing.
           */
          if (r.refused.length > 0) {
            setNamingError(
              `${r.refused.length} leg${r.refused.length === 1 ? '' : 's'} stayed out: ${r.refused[0]!.why}`,
            )
          } else {
            setNaming(null)
          }
          grouping.clear()
        } else {
          await api.patch(`/trades/${naming.tradeId}/label`, { label })
          setNaming(null)
        }
        refresh()
      } catch (e: unknown) {
        setNamingError(e instanceof Error ? e.message : 'That could not be saved.')
      } finally {
        setNamingBusy(false)
      }
    },
    [naming, grouping, refresh],
  )
  const held = useMemo(() => [...split.baskets, ...split.manual], [split])

  /*
   * ── WHAT THIS BOOK CAN LOSE TODAY — 6.12 ────────────────────────────────
   *
   * Computed from the SAME `held` list the pane draws, so the headline and
   * the cards under it can never disagree about what is at stake — the
   * lesson of the badge that once read "Positions 50" over an empty pane.
   *
   * Pooled per underlying rather than summed per card: two baskets lose at
   * different spots, so adding their worst cases would describe a day that
   * cannot happen. See `useOpenRisk`.
   */
  const risk = useOpenRisk(held)

  /*
   * Closing orders — ROADMAP 5.12.
   *
   * Sends WHICH positions and WHICH TRADE, never how much. The server
   * re-reads the broker at the moment of acting and caps the quantity at
   * what that trade holds (`ExitCaps`), because two baskets can share a
   * strike and the broker reports one netted row for both — so exiting by
   * token alone would close someone else's basket as well.
   *
   * Deliberately NOT behind a confirmation. An exit reduces risk, and a
   * dialog that slows down getting flat is a hazard rather than a safeguard.
   * The button disables itself while one is in flight, which is what stops a
   * double-click; the idempotency key stops the rest.
   */
  /*
   * ── NO PRE-EXIT CHECK — 7.7 RETIRED ─────────────────────────────────────
   *
   * This used to ask `/portfolio/exit-check` first and, on a mismatch, open
   * a dialog explaining that the order would open a position rather than
   * close one.
   *
   * It is gone by decision. The check compared our legs against the
   * BROKER's netted row, and once `planExitByLeg` started sizing from our
   * own record the dialog was answering a question the exit no longer asks.
   * What remained was a confirmation on the one path that must never make a
   * trader hesitate — getting out — plus a round trip to the broker before
   * every basket exit.
   *
   * THE COST, STATED: when the contract genuinely is gone — squared off in
   * the broker's own terminal, or expired — the closing order reaches an
   * exchange that is flat on it and OPENS a position. Nothing warns about
   * that any more. The leg marked for review (7.8) is still the right way
   * to clear a position that stopped being real; this button is for one
   * that is.
   */
  /*
   * ── 9.34. A RESTING ORDER IS CLEARED BEFORE THE EXIT, NOT AROUND IT ──────
   *
   * The server reserves a resting closing order's quantity, so a leg wholly
   * covered by one is excluded from a new exit — the guard that stops the
   * same holding being closed twice. Its consequence was reported on
   * 2026-11-03: a 50% exit on a four-leg basket with one leg resting filled
   * three and left the fourth, and the basket sat half-off around a working
   * order nobody had mentioned.
   *
   * So the exit CANCELS first and then closes everything together. Two
   * things make that safe to do in this order:
   *
   *   it is CONFIRMED. Cancelling a trader's own stop or limit is a
   *   decision, not a side effect, and the dialog names how many go.
   *
   *   it WAITS for the cancels to land. Sending a market order over an
   *   order that is still live at the exchange is the double-send this
   *   whole path exists to prevent — so the exit goes only once the book
   *   says nothing is working on those legs, and says so plainly if the
   *   cancels have not landed in time.
   */
  const [pendingExit, setPendingExit] = useState<
    | {
        resting: RestingExit[]
        run: () => void
      }
    | null
  >(null)
  const [clearing, setClearing] = useState(false)

  /**
   * Wait until these order rows stop working, or give up.
   *
   * Reads the ORDER STORE rather than re-fetching: it is already kept live
   * by the tick socket's book updates, so this is a wait on a fact arriving
   * rather than a poll of the server. Six seconds is generous for a cancel
   * and short enough that a trader is not left staring.
   */
  const ordersRef = useRef(orders)
  ordersRef.current = orders
  const waitForCancelled = useCallback(async (rowIds: readonly string[]): Promise<boolean> => {
    const deadline = Date.now() + 6_000
    const stillWorking = () =>
      ordersRef.current.some((o) =>
        o.legs.some(
          (l) => l.orderRowId != null && rowIds.includes(l.orderRowId) && isWorking(l.state),
        ),
      )
    while (Date.now() < deadline) {
      if (!stillWorking()) return true
      await new Promise((r) => setTimeout(r, 250))
    }
    return !stillWorking()
  }, [])

  const guardResting = useCallback(
    (trades: readonly OpenBookTrade[], tokens: readonly number[] | undefined, run: () => void) => {
      const resting = restingExitsFor(trades, orders, tokens)
      if (resting.length === 0) {
        run()
        return
      }
      setPendingExit({ resting, run })
    },
    [orders],
  )

  const confirmResting = useCallback(async () => {
    if (!pendingExit) return
    setClearing(true)
    try {
      for (const r of pendingExit.resting) {
        await api.post(`/trades/orders/${r.orderRowId}/cancel`, {})
      }
      /*
       * The cancel is a REQUEST, not a fact. Poll our own book until the
       * exchange has taken them off, because the server still reserves a
       * working order's quantity and an exit sent now would simply be
       * skipped again — or worse, land beside an order still live.
       */
      const gone = await waitForCancelled(pendingExit.resting.map((r) => r.orderRowId))
      if (!gone) {
        setExitError(
          'The pending orders are still cancelling. Give it a moment and press exit again.',
        )
        setPendingExit(null)
        return
      }
      const run = pendingExit.run
      setPendingExit(null)
      run()
    } catch (e: unknown) {
      setExitError(
        e instanceof Error ? e.message : 'The pending orders could not be cancelled.',
      )
      setPendingExit(null)
    } finally {
      setClearing(false)
    }
  }, [pendingExit])

  const exitTrade = useCallback(
    (trade: OpenBookTrade) => {
      guardResting([trade], undefined, () => {
        void exit(
          { connectionId: trade.broker.connectionId, tradeIds: [trade.tradeId] },
          trade.tradeId,
          trade.broker,
          // `tradeName`, not `strategyName`: a trade the trader NAMED would
          // otherwise be confirmed as its underlying — "Exit NIFTY" for a
          // basket called "Long Short Strangle with ATM". Same omission the
          // payoff picker had.
          `Exit ${tradeName(trade) ?? trade.underlying}`,
        )
      })
    },
    [exit, guardResting],
  )

  /**
   * Book part of a whole basket — ROADMAP 9.29.
   *
   * The same request as `exitTrade` with a `fraction` on it, which is the
   * entire difference: the server resolves the percentage against each
   * leg's OWN open lots at the moment of acting, so the browser never
   * asserts a size and a basket of unequal legs stays in proportion.
   *
   * Tracked under the trade id like a full exit, because it is the same
   * button's worth of work and two of them must not run at once.
   */
  /**
   * Open the ticket with the WHOLE basket as its subject — 9.36.
   *
   * The same panel a leg's own control opens, so the gesture is learned
   * once. `guardResting` still runs first: a basket with a limit resting on
   * one of its legs has to clear it before any of this means anything.
   */
  const exitTradePart = useCallback(
    (trade: OpenBookTrade, anchor: HTMLElement) => {
      guardResting([trade], undefined, () => {
        setExitTicket({ trade, leg: null, anchor, requestId: crypto.randomUUID() })
      })
    },
    [guardResting],
  )

  /*
   * ── EXIT EVERY MANUAL LEG, ON EVERY ACCOUNT — 5.12 ──────────────────────
   *
   * ONE REQUEST PER BROKER ACCOUNT. Within an account that is still one
   * request naming every trade, not one per trade: the server plans the
   * whole set together, so `orderExitLegs` sends BUYs first across it —
   * closing the shorts before the longs, which is what makes a partial
   * failure leave the safer book. N separate requests could not do that, and
   * each would race the others' double-press guard.
   *
   * ACROSS accounts it has to be N requests, because an order is addressed
   * to ONE connection — a `BrokerOrder` names it, the session that signs it
   * is per account, and an intent carrying two would have no broker to go
   * to. That is a real constraint and it is not what was wrong here.
   *
   * What was wrong: this REFUSED when the rows spanned two accounts, and
   * told the trader to exit them one account at a time. On the one screen
   * whose job is getting flat, with ten legs across ME01045 and M000295,
   * that is the worst available answer — the software could address both and
   * declined to. Reported 2026-10-30.
   *
   * So the fan-out happens here, where each row's account is already known
   * and the rows are already drawn with a BROKER column. Each group gets its
   * own intent, its own idempotency key, its own in-flight marker and its
   * own card state, and they go out CONCURRENTLY: two brokers are two
   * independent dispatch lanes in the engine, and waiting for the first to
   * answer before asking the second would add a round trip to getting flat
   * for no gain in safety.
   */
  const exitManual = useCallback(
    (manualTrades: readonly OpenBookTrade[]) => {
      const byAccount = new Map<string, OpenBookTrade[]>()
      for (const trade of manualTrades) {
        const group = byAccount.get(trade.broker.connectionId)
        if (group) group.push(trade)
        else byAccount.set(trade.broker.connectionId, [trade])
      }
      if (byAccount.size === 0) return

      void (async () => {
        const results = await Promise.all(
          [...byAccount.values()].map(async (group) => {
            const first = group[0]!
            const account = first.broker.label ?? 'this account'
            const legs = group.reduce(
              (n, t) => n + t.legs.filter((l) => l.netQty !== 0).length,
              0,
            )
            const outcome = await exit(
              {
                connectionId: first.broker.connectionId,
                tradeIds: group.map((t) => t.tradeId),
              },
              manualExitKey(first.broker.connectionId),
              first.broker,
              `Exit ${legs} manual leg${legs === 1 ? '' : 's'} · ${account}`,
            )
            return { account, outcome }
          }),
        )

        /*
         * ── ONE BANNER THAT NAMES EVERY ACCOUNT ─────────────────────────
         *
         * `useExitOrder` writes the banner itself, and with one account that
         * sentence is complete — nothing is added. With two it is not: both
         * write the same `error` string, the second wins, and the first
         * account's fate silently disappears. "One of your two brokers was
         * never asked" is exactly the thing a trader must not have to infer
         * from a list not changing.
         *
         * REFUSED and UNCONFIRMED are kept apart on purpose. A send that
         * never answered may have reached the broker (§5.6), so folding it
         * into "not exited" would invite the retry that doubles a position.
         */
        const refused: { account: string; why: string }[] = []
        const unconfirmed: { account: string; why: string }[] = []
        let accepted = 0
        for (const { account, outcome } of results) {
          if (outcome.kind === 'ACCEPTED') accepted += 1
          else if (outcome.kind === 'REFUSED')
            refused.push({ account, why: outcome.why })
          else if (outcome.kind === 'UNCERTAIN')
            unconfirmed.push({ account, why: outcome.why })
        }
        if (
          results.length < 2 ||
          (refused.length === 0 && unconfirmed.length === 0)
        )
          return

        const name = (
          list: readonly { account: string; why: string }[],
        ): string => list.map((r) => `${r.account} — ${r.why}`).join(' · ')
        setExitError(
          [
            `${accepted} of ${results.length} accounts accepted the exit.`,
            refused.length > 0 ? `Rejected: ${name(refused)}.` : '',
            unconfirmed.length > 0
              ? `UNCONFIRMED, and may have reached the broker — check before retrying: ${name(unconfirmed)}.`
              : '',
          ]
            .filter(Boolean)
            .join(' '),
        )
      })()
    },
    [exit, setExitError],
  )

  const exitLeg = useCallback(
    (trade: OpenBookTrade, leg: OpenBookLeg) => {
      if (leg.token === null) {
        setExitError(
          'That leg has no broker token, so an order cannot be addressed to it.',
        )
        return
      }
      const token = leg.token
      guardResting([trade], [token], () => {
        void exit(
        {
          connectionId: trade.broker.connectionId,
          tradeIds: [trade.tradeId],
          tokens: [token],
          /*
           * ── THIS ROW, NOT THIS CONTRACT ────────────────────────────────
           *
           * `tokens` alone means "everything the trade holds on this
           * contract", and a basket may legitimately hold a strike TWICE —
           * `legTransferPolicy` allows same-contract, same-direction legs
           * into a basket on purpose, because an Iron Condor that holds its
           * short strike twice after an adjustment is a five-leg structure
           * and collapsing two of them would make the deployment's record
           * claim a leg it no longer has.
           *
           * Without the leg id the server planned one order PER LEG on the
           * token: pressing one cross on a doubled strike sent two SELL 65s
           * and closed both rows. Reported 2026-11-03 with the order book
           * showing the pair at the same second.
           *
           * 5.15 built the narrow scope and the exit TICKET has always used
           * it; the fast cross never did. `tokens` stays alongside it —
           * together they say "this leg, and only on this contract".
           */
          tradeLegId: leg.tradeLegId,
        },
          leg.tradeLegId,
          trade.broker,
          `Exit ${leg.symbol}`,
        )
      })
    },
    [exit, guardResting],
  )

  /*
   * ── THE EXIT TICKET — ROADMAP 5.15 ──────────────────────────────────────
   *
   * The row's × above is "close this leg, now, at market" and stays one
   * click. This opens the ticket: a quarter of it, or an order that rests.
   *
   * It goes through the SAME `exit` as every other closing order — the same
   * pending map, the same per-leg double-press guard, the same order-book
   * row. A ticket that posted somewhere else would be a second exit path
   * with its own guards to keep in step, and the first thing to drift would
   * be the one that stops a second press sizing against a leg whose first
   * exit has not landed.
   */
  /*
   * ── STABLE, SO THE LIST CAN BAIL OUT — ROADMAP 9.60 ────────────────────
   *
   * `PositionsBody` is memoised below, and these two were the only props
   * it received that were rebuilt on every render — an object literal and
   * an inline arrow. Either one on its own defeats the memo entirely, so
   * the list re-rendered its fifteen cards and forty leg rows every time
   * ANY state in this component changed, including opening and closing the
   * exit ticket.
   *
   * Anything added to this component's props must be stable too, or the
   * memo silently stops doing anything. That is the cost of the pattern
   * and the reason it is written down here rather than left to be noticed.
   */
  const openGroupDialog = useCallback(() => {
    setNamingError(null)
    setNaming({ kind: 'GROUP' })
  }, [])

  const openRenameDialog = useCallback((trade: OpenBookTrade) => {
    setNamingError(null)
    setNaming({ kind: 'RENAME', tradeId: trade.tradeId, initial: trade.label ?? '' })
  }, [])

  const groupingApi = useMemo(
    () => ({
      selected: grouping.selected,
      onToggle: grouping.toggle,
      onGroup: openGroupDialog,
      busy: namingBusy,
    }),
    [grouping.selected, grouping.toggle, openGroupDialog, namingBusy],
  )

  const [exitTicket, setExitTicket] = useState<ExitTicketTarget | null>(null)
  const openExitTicket = useCallback((trade: OpenBookTrade, leg: OpenBookLeg, anchor: HTMLElement) => {
    setExitTicket({ trade, leg, anchor, requestId: crypto.randomUUID() })
  }, [])

  const submitExitTicket = useCallback(
    async (req: ExitTicketRequest): Promise<{ ok: boolean; reason?: string }> => {
      /*
       * ── SCALING IN IS AN ENTRY, NOT A SMALLER EXIT — ROADMAP 9.61 ─────
       *
       * It goes to its own endpoint rather than the chain's order path,
       * because the legs it adds to have to be NAMED. `manualNetting`
       * discovers a leg for a chain click and refuses a grouped one, so a
       * browser-built order would open a stray position beside the basket
       * instead of growing it. `scaleInTrade` resolves the legs
       * server-side from the trader's own record and links the fills to
       * them — see its header.
       *
       * The browser therefore sends WHICH position and WHAT FRACTION, and
       * never a quantity. The holding is read at the moment of acting, so
       * a screen a few seconds stale cannot name a size the position no
       * longer justifies.
       */
      if (req.direction === 'IN') {
        return api
          .post<{ ok: boolean; reason?: string }>('/market/scale-in', {
            connectionId: req.connectionId,
            tradeId: req.tradeId,
            ...(req.tradeLegId === undefined ? {} : { tradeLegId: req.tradeLegId }),
            fraction: req.fraction,
            clientRequestId: crypto.randomUUID(),
            label: req.label,
          })
          .then((r) => ({ ok: r.ok, ...(r.reason === undefined ? {} : { reason: r.reason }) }))
          .catch((e: unknown) => ({
            ok: false,
            reason: e instanceof Error ? e.message : 'The add was not accepted.',
          }))
      }

      const outcome = await exit(
        {
          connectionId: req.connectionId,
          tradeIds: [req.tradeId],
          tradeLegId: req.tradeLegId,
          fraction: req.fraction,
          ...(req.limitPrice === undefined ? {} : { limitPrice: req.limitPrice }),
        },
        /*
         * 9.36. The busy key is the LEG when one was named, else the TRADE
         * — a basket's partial is the same gesture as its EXIT and must
         * not run twice at once.
         */
        req.tradeLegId ?? req.tradeId,
        /*
         * The broker label comes from the TRADE, which the ticket carries —
         * the drawer shows every account at once, so the header's selector
         * is the wrong source for a row that belongs to a specific one.
         */
        exitTicket?.trade.broker ?? { connectionId: req.connectionId, label: '', mode: 'PAPER' },
        req.label,
      )
      /*
       * IGNORED means a press for this leg was already in flight, which is
       * the guard doing its job rather than a failure. The dialog closes on
       * it: the exit the trader asked for is already on its way.
       */
      if (outcome.kind === 'ACCEPTED' || outcome.kind === 'IGNORED') return { ok: true }
      return { ok: false, reason: outcome.why }
    },
    [exit, exitTicket],
  )

  // ROADMAP 6.10. The journal comes from the server, the marks from the tick
  // store; see `useDayTotals` for why neither side can supply the other's.
  const day = useDayTotals(refreshToken + refreshNonce + bookRevision)

  // Only what is DRAWN needs a mark. A flagged or unopened trade has no row
  // to put a price in.
  const tokens = useMemo(() => tokensOf(held), [held])
  useTickSubscription(tokens)

  /** Legs actually held, in the trades the pane actually draws. */
  const posCount = useMemo(
    () =>
      held.reduce((n, t) => n + t.legs.filter((l) => l.netQty !== 0).length, 0),
    [held],
  )
  /*
   * ── COUNT WHAT THE TABLE RENDERS — the LEG, not the intent ──────────────
   *
   * These counted `intent.state` while every row below renders `leg.state`,
   * and the two do not agree: the reconciler settles a LEG, and nothing
   * walks that conclusion back up. Seen on 2026-10-26 as "UNKNOWN 3" over an
   * order book with no unknown row in it — three intents still AMBIGUOUS
   * whose legs had already been settled REJECTED.
   *
   * The same mismatch made a "Rejected" row un-clickable earlier the same
   * day. A badge that disagrees with the list under it is worse than no
   * badge, because it is the one people act on.
   */
  const legStates = orders.flatMap((o) =>
    o.legs.length > 0 ? o.legs.map((l) => l.state) : [o.state],
  )
  const refusedCount = legStates.filter(
    (st) => st === 'REFUSED' || st === 'REJECTED',
  ).length
  /*
   * AMBIGUOUS is counted on its own and kept OUT of "working". `isWorking`
   * groups them because both may be live at the exchange, but the two are not
   * the same thing to a human: working means the broker acknowledged the
   * order, AMBIGUOUS means we never learned whether it exists. Showing it as
   * "working" would claim knowledge we do not have, and the one action the
   * user must not take on it is placing it again.
   */
  const unresolvedCount = legStates.filter((st) => st === 'AMBIGUOUS').length
  const workingCount = legStates.filter(
    (st) => isWorking(st) && st !== 'AMBIGUOUS',
  ).length

  return (
    <>
      <PayoffDock
        open={payoffOpen}
        onClose={() => onPayoffClose?.()}
        book={book}
        onRefresh={refreshBook}
      />
      <aside
        className={cn('tr-drawer', open && 'open')}
        aria-label="Positions and orders"
      >
        {/* ── collapsed rail ──────────────────────────────────────────── */}
        <div className="tr-rail" aria-hidden={open}>
          <button
            type="button"
            className="tr-railbtn"
            aria-label="Expand panel"
            title="Expand"
            onClick={() => onOpen(tab)}
            tabIndex={open ? -1 : 0}
          >
            <ChevronLeft size={18} />
          </button>
          <span
            style={{
              width: 22,
              height: 1,
              background: 'hsl(var(--border))',
              margin: '4px 0',
            }}
          />
          <button
            type="button"
            className="tr-railbtn"
            aria-label="Positions"
            title="Positions"
            onClick={() => onOpen('POSITIONS')}
            data-testid="rail-positions"
            tabIndex={open ? -1 : 0}
          >
            <Wallet size={18} />
            {book.kind === 'FAILED' ? (
              <span
                className="tr-count unknown"
                title="The open book could not be read"
              >
                ?
              </span>
            ) : (
              posCount > 0 && (
                <span className="tr-count neutral">{posCount}</span>
              )
            )}
          </button>
          <button
            type="button"
            className="tr-railbtn"
            aria-label="Orders"
            title="Orders"
            onClick={() => onOpen('ORDERS')}
            data-testid="rail-orders"
            tabIndex={open ? -1 : 0}
          >
            <LayoutList size={18} />
            {/* Unresolved outranks refused on the badge: a refusal is over and
              cost nothing, an unresolved order may be a live position. */}
            {unresolvedCount > 0 ? (
              <span className="tr-count unknown" data-testid="rail-unresolved">
                {unresolvedCount}
              </span>
            ) : (
              refusedCount > 0 && (
                <span className="tr-count">{refusedCount}</span>
              )
            )}
          </button>
          <div className="flex-1" />
          {/*
          The rail is 56px of VERTICAL text, so it gets the number and not
          the "3 of 4" chip — there is no room for a caveat, and a caveat
          that has to be squinted at is worse than one in the tooltip. The
          full treatment is in the open tile below.
        */}
          <span
            className="tr-vlabel"
            title={dayTitle(day)}
            data-testid="day-rail"
          >
            Day MTM
            <b className={cn('num', dayToneOf(day))}>{dayRailText(day)}</b>
          </span>
        </div>

        {/* ── open body ───────────────────────────────────────────────── */}
        <div className="tr-dbody" aria-hidden={!open}>
          <div className="tr-dhead">
            <div className="tr-tabs" role="tablist" aria-label="Book">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'POSITIONS'}
                className={cn('tr-tab', tab === 'POSITIONS' && 'on')}
                onClick={() => onOpen('POSITIONS')}
                data-testid="tab-positions"
                tabIndex={open ? 0 : -1}
              >
                {/* An em dash when the book could not be read. A zero there
                  would be the one claim this panel must never make: that
                  nothing is open when nobody knows. */}
                Positions
                <span className="tr-tc num">
                  {book.kind === 'READY' ? posCount : '—'}
                </span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'ORDERS'}
                className={cn('tr-tab', tab === 'ORDERS' && 'on')}
                onClick={() => onOpen('ORDERS')}
                data-testid="tab-orders"
                tabIndex={open ? 0 : -1}
              >
                Orders
                <span className={cn('tr-tc num', refusedCount > 0 && 'alert')}>
                  {orderRowsToday.length}
                </span>
              </button>
            </div>
            {/*
            ── THE WAY BACK HAS TO BE A LABEL, NOT A CHEVRON ───────────────
            In full screen the chain is gone and the only controls left are
            a drag handle at the very edge and this button. A bare `>` read
            as "collapse" — which it was, and collapsing from full screen
            left an empty workspace, because the chain was still hidden and
            the drawer no longer covered it.

            So when the chain is hidden this becomes a WORDED control that
            names where it goes. Nobody has to guess that an arrow means
            "bring the ladder back", and Escape does the same thing for
            anyone who never looks at it.
          */}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {chainHidden && onPayoffOpen && (
                <button
                  type="button"
                  className="tr-backchain"
                  onClick={onPayoffOpen}
                  data-testid="book-payoff-chart"
                  tabIndex={open ? 0 : -1}
                >
                  <LineChart size={15} />
                  <span>Payoff Chart</span>
                </button>
              )}
              {chainHidden ? (
                <button
                  type="button"
                  className="tr-backchain"
                  onClick={onShowChain}
                  data-testid="show-chain"
                  title="Show the option chain (Esc)"
                  tabIndex={open ? 0 : -1}
                >
                  <PanelLeftOpen size={15} />
                  <span>Exit Full screen</span>
                  <kbd className="tr-kbd sr-only">Esc</kbd>
                </button>
              ) : (
                <>
                  {onFullScreen && (
                    <button
                      type="button"
                      className="tr-backchain"
                      onClick={onFullScreen}
                      data-testid="book-full-screen"
                      title="Expand the positions and orders panel to full screen"
                      tabIndex={open ? 0 : -1}
                    >
                      <Maximize2 size={14} />
                      <span>Full screen</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="tr-railbtn"
                    style={{ width: 36, height: 36 }}
                    aria-label="Collapse panel"
                    title="Collapse"
                    onClick={onClose}
                    data-testid="drawer-close"
                    tabIndex={open ? 0 : -1}
                  >
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/*
          ── ONE LINE, NOT THREE BOXES ─────────────────────────────────────
          These were three bordered tiles, 12px of padding each, stacking
          label over value — about 70px of vertical space to carry one
          number that matters and two that are usually zero.

          Day MTM is the figure a trader actually watches, so it keeps its
          size and leads. The counts follow it as inline pairs, and the two
          that are normally zero are DROPPED ENTIRELY when they are, rather
          than shown as a zero that has to be read to be dismissed.
        */}
          <div className="tr-stats" data-testid="drawer-stats">
            <div className="tr-stat lead" data-testid="day-mtm">
              <span className="tr-flabel" title={dayTitle(day)}>
                Day MTM
              </span>
              <DayMtmValue day={day} className="tr-sval num" />
            </div>
            {/*
              9.64. Between the two, because it reads between them: Day MTM
              is today and includes what today banked, this is what is
              still held against what it was entered at, and OPEN RISK is
              what that could still cost.
            */}
            {book.kind === 'READY' && <OpenMtm trades={book.trades} />}
            {/*
              OPEN RISK SITS BESIDE DAY MTM, and the pairing is the point:
              one is what has happened, the other is what still can. A
              trader reading only the first is reading half the position.
            */}
            <div className="tr-stat" data-testid="open-risk">
              <OpenRiskChip risk={risk.total} size="lg" testId="open-risk-total" />
            </div>
            <div className="tr-statline">
              {workingCount > 0 && (
                <span className="tr-pair" data-testid="stat-working">
                  <span className="tr-flabel">Working</span>
                  <span className="num">{workingCount}</span>
                </span>
              )}
              {unresolvedCount > 0 && (
                <span className="tr-pair warn" data-testid="orders-unresolved">
                  <span className="tr-flabel">Unknown</span>
                  <span className="num">{unresolvedCount}</span>
                </span>
              )}
              {refusedCount > 0 && (
                <span className="tr-pair bad" data-testid="stat-refused">
                  <span className="tr-flabel">Rejected</span>
                  <span className="num">{refusedCount}</span>
                </span>
              )}
              {/*
                NO "nothing working" PLACEHOLDER.
                The three counts above already vanish when they are zero,
                which IS the message — an empty status line says nothing is
                working more clearly than a sentence saying so, and without
                occupying the row every single day. Removed on the owner's
                instruction after it survived an earlier pass.
              */}
            </div>
          </div>

          {exitNotice ? (
            <div
              role="status"
              aria-live="polite"
              className="mx-3 mb-2 flex items-start gap-2.5 rounded border border-primary/25 bg-primary/5 px-3 py-2.5 text-xs text-foreground"
              data-testid="exit-progress"
            >
              {exiting.size > 0 ? (
                <span
                  className="mt-0.5 h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
                  aria-hidden="true"
                />
              ) : (
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-success" />
              )}
              {/*
                * Headline and reason stack rather than running together: the
                * first is read at a glance mid-session, the second is read
                * once. One long sentence buried the actionable half.
                */}
              <span className="flex-1 leading-snug">
                <span className="font-medium">{exitNotice.headline}</span>
                {exitNotice.detail ? (
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    {exitNotice.detail}
                  </span>
                ) : null}
              </span>
              {exiting.size > 0 ? (
                <button
                  type="button"
                  onClick={refresh}
                  className="mt-0.5 shrink-0 underline underline-offset-2"
                >
                  Refresh status
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="tr-dscroll" ref={dragScrollPort}>
            {tab === 'POSITIONS' ? (
              <PositionsBody
                book={book}
                split={split}
                risk={risk}
                clock={clock}
                onExitLeg={exitLeg}
                onExitTicket={openExitTicket}
                onExitPart={exitTradePart}
                onExitTrade={exitTrade}
                onExitManual={exitManual}
                onArmed={patchLeg}
                onRetry={refreshBook}
                exiting={exiting}
                drag={legDrag}
                grouping={groupingApi}
                onRename={openRenameDialog}
              />
            ) : (
              <OrdersBody orders={orders} rows={orderRowsToday} />
            )}
            {/*
              9.34. Cancelling a trader's own stop or limit is a decision,
              not a side effect of pressing exit — so it is named, counted
              and priced before anything is sent.
            */}
            <ConfirmDialog
              open={pendingExit !== null}
              onOpenChange={(o) => {
                if (!o && !clearing) setPendingExit(null)
              }}
              title={`Cancel ${restingCountLabel(pendingExit?.resting.length ?? 0)} first?`}
              destructive
              confirmLabel="Cancel them and exit"
              cancelLabel="Leave them"
              isPending={clearing}
              pendingLabel="Cancelling…"
              onConfirm={() => void confirmResting()}
              description={
                <span className="block space-y-1.5">
                  <span className="block">
                    This position has working orders on it. They are cancelled
                    first, then every leg exits together — otherwise the legs
                    they cover are left behind.
                  </span>
                  <span className="block space-y-0.5">
                    {(pendingExit?.resting ?? []).map((r) => (
                      <span key={r.orderRowId} className="num block text-[11.5px]">
                        {r.symbol ?? r.token}
                        {r.limitPrice != null && ` · resting at ${r.limitPrice.toFixed(2)}`}
                      </span>
                    ))}
                  </span>
                </span>
              }
            />
            {naming && (
              <NameGroupDialog
                title={naming.kind === 'GROUP' ? 'Name this group' : 'Rename this position'}
                {...(naming.kind === 'RENAME' ? { initial: naming.initial } : {})}
                {...(naming.kind === 'GROUP' ? { legCount: grouping.selected.size } : {})}
                busy={namingBusy}
                error={namingError}
                {...(naming.kind === 'GROUP' ? { warning: grouping.warning } : {})}
                onSubmit={(label) => void submitName(label)}
                onClose={() => setNaming(null)}
              />
            )}
          </div>

          <div className="tr-dfoot">
            {/* TODO: cancel-open needs an order-cancel intent kind. Disabled
              rather than absent, so the panel's shape does not move when it
              arrives. */}
            <button
              type="button"
              className="tr-btn"
              style={{ flex: 1 }}
              disabled
              title="Not built yet"
            >
              Cancel open
            </button>
            {/*
            ── NO SQUARE OFF ALL ─────────────────────────────────────────────
            Removed deliberately, not misplaced. "Flatten the account" closes
            everything the BROKER holds — including positions opened in its
            own terminal that belong to no trade here — and it sat beside the
            per-basket Exit buttons as though it were the same kind of action
            at a larger size. It is not: it is the one control on this panel
            whose blast radius is the whole account.

            Exiting is per trade or per leg, where the thing being closed is
            named and on screen. The server path still exists; an
            account-wide flatten belongs behind a deliberate screen rather
            than beside the button a trader clicks all day.
          */}
          </div>
          {/*
          ── WHAT IS THIS LEG FOR? — 9.10 ──────────────────────────────────
          Only after a leg joins a BASKET. The move has already committed by
          the time this opens, so Skip and Escape are answers rather than
          cancellations — and the role stays editable from the leg row,
          which is what makes skipping safe to offer.
        */}
          <LegRoleDialog
            ask={
              landed && landed.ask
                ? {
                    tradeLegId: landed.tradeLegId,
                    symbol: landed.symbol,
                    into: landed.into,
                  }
                : null
            }
            saving={savingRole !== null}
            onSkip={dismiss}
            onChoose={(role) => {
              if (landed) void setRole(landed.tradeLegId, role)
              dismiss()
            }}
          />

          {exitError && (
            <div
              className="tr-reason"
              style={{ margin: '0 14px 12px' }}
              data-testid="exit-error"
            >
              {exitError}
            </div>
          )}

          {/*
            5.15. Part of a leg, or an order that rests. Mounted here beside
            the role dialog rather than inside the row that opened it: the
            drawer scrolls, and a dialog owned by a row unmounts the moment
            that row is virtualised or the book is re-read under it.
          */}
          <ExitTicket
            target={exitTicket}
            onClose={() => setExitTicket(null)}
            onSubmit={submitExitTicket}
          />
        </div>
      </aside>
    </>
  )
}

const EMPTY_TRADES: readonly OpenBookTrade[] = []

/**
 * The positions pane — our own open trades, across every account.
 *
 * THREE STATES, AND "NOTHING OPEN" IS ONLY ONE OF THEM. A failed read is
 * not an empty book, and it says so rather than rendering the reassuring
 * empty state over a question nobody answered.
 */
/**
 * The positions pane — ROADMAP 9.60.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * MEMOISED BECAUSE OPENING A POPUP MUST NOT REDRAW THE BOOK
 *
 * `exitTicket` lives in `BookDrawer`, which also renders this. Every open
 * and every close therefore re-rendered fifteen trade cards and some forty
 * leg rows to show a 300px panel — which is the lag reported on
 * 2026-11-05, on both the opening and the closing.
 *
 * The state is deliberately NOT moved. It belongs with the exit handlers
 * that read it, and lifting it would spread one feature across two
 * components to buy what one `memo` buys here.
 *
 * Every prop above is a `useMemo`, a `useCallback` or a value that only
 * changes when the book does. See the note beside `groupingApi`: one
 * inline object is all it takes to turn this back off.
 * ─────────────────────────────────────────────────────────────────────────
 */
const PositionsBody = memo(function PositionsBody({
  book,
  split,
  risk,
  clock,
  onExitLeg,
  onExitTicket,
  onExitTrade,
  onExitManual,
  onArmed,
  onRetry,
  exiting,
  drag,
  grouping,
  onRename,
  onExitPart,
}: {
  book: OpenBookState
  /** Computed by the caller, so the badge and the pane cannot disagree. */
  split: SplitBook
  /** 6.12. Computed from the same split, for the same reason. */
  risk: BookRisk
  clock: string
  onExitLeg: (trade: OpenBookTrade, leg: OpenBookLeg) => void
  onExitTicket?: (trade: OpenBookTrade, leg: OpenBookLeg, anchor: HTMLElement) => void
  onExitTrade: (trade: OpenBookTrade) => void
  /** 5.12. Close every manual leg in one request. */
  onExitManual: (trades: readonly OpenBookTrade[]) => void
  onArmed: Parameters<typeof TradeCard>[0]['onArmed']
  onRetry: () => void
  /** Everything currently being exited: tradeIds, tradeLegIds, manual keys. */
  exiting: ReadonlySet<string>
  /** 9.9. Dragging a leg from one trade to another. */
  drag?: LegDragApi
  /** 9.26. Ticking loose legs so they can be named as one basket. */
  grouping?: Parameters<typeof ManualLegs>[0]['grouping']
  /** 9.26. Renaming a basket the trader named. Absent on a strategy's. */
  onRename?: (trade: OpenBookTrade) => void
  /** 9.36. Opening the exit ticket on a whole basket. */
  onExitPart?: (trade: OpenBookTrade, anchor: HTMLElement) => void
}) {
  if (book.kind === 'LOADING') return <Note>Reading your open trades…</Note>

  if (book.kind === 'FAILED') {
    return (
      <Notice
        tone="error"
        title="The open book could not be read."
        testId="open-book-failed"
        action={
          <button
            type="button"
            onClick={onRetry}
            data-testid="open-book-retry"
            className="rounded px-1.5 py-0.5 text-[11px] font-semibold underline underline-offset-2 hover:opacity-80"
          >
            Try again
          </button>
        }
      >
        This is not the same as holding nothing — what is open is unknown until
        this read succeeds. {book.why}
      </Notice>
    )
  }

  const { baskets, manual, unopened } = split
  const nothingHeld = baskets.length === 0 && manual.length === 0

  if (nothingHeld && unopened.length === 0) {
    return (
      <div className="tr-empty" data-testid="positions-flat">
        <span className="tr-emptyicon">
          <CheckCircle2 size={24} />
        </span>
        <span className="mt-1.5 text-[16px] font-semibold">Nothing open</span>
        <span className="max-w-[290px] text-[12.5px] leading-[1.55] text-muted-foreground">
          No open trades on any of your accounts. Fire a strategy, or click B or
          S on the chain, and it appears here.
        </span>
        <span className="num mt-1 text-[11px] text-muted-foreground">
          Read {clock} IST
        </span>
      </div>
    )
  }

  /*
   * BUSY IS PER CARD, NOT PER DRAWER.
   *
   * Every card used to be disabled while any exit was in flight, so EXIT
   * ALL on one basket locked EXIT ALL on all of the others until the first
   * settled — reported on 2026-10-27 with ten positions open. The rule that
   * was reaching for is about a LEG being closed twice, and it lives in
   * `useExitOrder` (and, authoritatively, in `exit.service.ts`, which sizes
   * every exit against what each leg still has outstanding).
   *
   * What remains here is presentation: this card is busy when something it
   * owns is being exited, and a basket with nothing in flight stays live.
   */
  const busyFor = (trade: OpenBookTrade): boolean =>
    trade.status === 'CLOSING' ||
    exiting.has(trade.tradeId) ||
    /*
     * A manual exit-all covers every trade in THAT BLOCK, and the block is
     * defined by `isGrouped` — the same predicate `splitBook` uses to decide
     * which list a trade lands in. This asked `strategyId === null` instead,
     * which is a different question: a CE+PE combo placed from the chain has
     * no strategy but DOES have a label, so it is drawn as a basket card and
     * was still being locked by the manual block's exit. Reported 2026-10-27
     * against "21,800 Long Straddle".
     */
    (exiting.has(manualExitKey(trade.broker.connectionId)) &&
      !isGrouped(trade)) ||
    trade.legs.some((l) => exiting.has(l.tradeLegId))

  /** The leg of THIS card that is exiting, when it is a leg rather than the basket. */
  const exitingLegOf = (trade: OpenBookTrade): string | null =>
    trade.legs.find((l) => exiting.has(l.tradeLegId))?.tradeLegId ?? null

  return (
    <>
      {nothingHeld && (
        <p
          className="mb-2 px-0.5 text-[12px] text-muted-foreground"
          data-testid="nothing-held"
        >
          Nothing held right now.
        </p>
      )}

      {baskets.map((trade) => (
        <TradeCard
          key={trade.tradeId}
          trade={trade}
          risk={risk.byTrade.get(trade.tradeId)}
          onExitLeg={onExitLeg}
          onExitTrade={onExitTrade}
          onArmed={onArmed}
          busy={busyFor(trade)}
          /*
           * Locked by THIS card's own work. `locked` disables the leg
           * crosses and dragging while the basket is closing, which is
           * still right — it just must not be driven by another card.
           */
          locked={busyFor(trade)}
          exitingLegId={exitingLegOf(trade)}
          {...(onExitTicket ? { onExitTicket } : {})}
          {...(onExitPart ? { onExitPart } : {})}
          {...(drag ? { drag } : {})}
          {/*
            9.26. Renameable only when no strategy owns the name. The
            server refuses the other case too; this keeps a control the
            trader can press from existing at all.
          */ ...(onRename && trade.strategyId === null && trade.label !== null
            ? { onRename }
            : {})}
        />
      ))}

      <ManualLegs
        trades={manual}
        onExitLeg={onExitLeg}
        {...(onExitTicket ? { onExitTicket } : {})}
        onExitAll={onExitManual}
        busyFor={busyFor}
        {...(drag ? { drag } : {})}
        {...(grouping ? { grouping } : {})}
      />
    </>
  )
})

/**
 * The day's P&L — ROADMAP 6.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE STATES, AND NONE OF THEM IS A ZERO
 *
 * A single number here would have to stand for five different things, and
 * four of them are not "the day is flat":
 *
 *   no account chosen       nothing was asked
 *   loading                 the answer is on its way
 *   the journal failed      we do not know, and must not imply we do
 *   nothing is marked       the open legs have no price
 *   partial                 some do, and the total says how many
 *
 * A zero is a real day P&L — a position exactly where it opened — so none of
 * the other four may render as one. This is the same rule the positions pane
 * follows for an unreadable book, applied to a number in a header where it
 * is easier to get wrong because a header looks like it should always have
 * a figure in it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function dayTitle(day: DayState): string {
  switch (day.kind) {
    case 'LOADING':
      return 'Reading the day journal…'
    case 'UNREADABLE':
      return `The day could not be read: ${day.why}`
    case 'READY': {
      const { totals } = day
      const base =
        `Booked ₹${totals.booked.toLocaleString('en-IN')} · ` +
        `charges ₹${totals.charges.toLocaleString('en-IN')}. ` +
        'A carried position is measured from the previous close, one opened today from its entry.'
      if (totals.net.kind === 'UNMARKED') {
        return `${base} No price yet on ${totals.net.unmarked} open leg(s), so there is no unbooked figure.`
      }
      if (totals.net.kind === 'PARTIAL') {
        return `${base} Covers ${totals.net.marked} of ${totals.net.marked + totals.net.unmarked} legs; the rest have no price yet.`
      }
      return base
    }
  }
}

/** The rail's one-glance text. Never a zero that means something unknown. */
function dayRailText(day: DayState): string {
  if (day.kind === 'LOADING') return '\u2026'
  if (day.kind !== 'READY') return '\u2014'
  const { net } = day.totals
  if (net.kind === 'UNMARKED') return '\u2014'
  return `${net.value >= 0 ? '+' : '-'}\u20b9${compactInr(Math.abs(net.value))}`
}

function dayToneOf(day: DayState): string {
  if (day.kind !== 'READY' || day.totals.net.kind === 'UNMARKED')
    return 'text-muted-foreground'
  const partial = day.totals.net.kind === 'PARTIAL'
  return cn(
    partial && 'opacity-70',
    day.totals.net.value >= 0 ? 'text-success' : 'text-destructive',
  )
}

function DayMtmValue({ day, className }: { day: DayState; className: string }) {
  if (day.kind !== 'READY') {
    return (
      <span
        className={cn(className, 'text-muted-foreground')}
        data-testid={`day-${day.kind.toLowerCase()}`}
      >
        {day.kind === 'LOADING' ? '…' : '—'}
      </span>
    )
  }

  const { net } = day.totals
  if (net.kind === 'UNMARKED') {
    return (
      <span
        className={cn(className, 'text-muted-foreground')}
        data-testid="day-unmarked"
      >
        —
      </span>
    )
  }

  const partial = net.kind === 'PARTIAL'
  return (
    <span className="flex items-center gap-1" data-testid="day-value">
      {partial && (
        <span
          className="num rounded bg-warning/15 px-1 py-0.5 text-[8px] font-extrabold text-warning"
          data-testid="day-partial"
        >
          {net.marked}/{net.marked + net.unmarked}
        </span>
      )}
      <span
        className={cn(
          className,
          partial && 'opacity-70',
          net.value >= 0 ? 'text-success' : 'text-destructive',
        )}
      >
        {net.value >= 0 ? '+' : '-'}₹{compactInr(Math.abs(net.value))}
      </span>
    </span>
  )
}

/**
 * The pill a trader reads down the STATUS column.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * REFUSED AND REJECTED BOTH READ "Rejected" — 9.36
 *
 * They are two states and the difference is real: REFUSED is OUR gate
 * saying no before the order ever left, REJECTED is the broker saying no
 * after it arrived. The engine keeps them apart and must go on doing so.
 *
 * But the COLUMN is not where that distinction lands. "Refused" and
 * "Rejected" sitting in one column, one word apart, read as the same thing
 * said twice — and the header counter already totals them together as
 * REJECTED, so the same order was "Rejected" above and "Refused" in its own
 * row.
 *
 * Nothing is lost: expanding the row still answers WHY from three separate
 * sources, and it is the origin that names itself there — our gate through
 * `refusedGate`, the broker through its own words on the leg. `title` says
 * it on hover without spending a column on it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const STATUS: Record<string, { cls: string; text: string; title?: string }> = {
  SUBMITTING: { cls: 'working', text: 'Submitting' },
  CREATED: { cls: 'working', text: 'Queued' },
  GATED: { cls: 'working', text: 'Checking' },
  SENDING: { cls: 'working', text: 'Sending' },
  SENT: { cls: 'working', text: 'Sent' },
  ACKED: { cls: 'working', text: 'Working' },
  PARTIAL: { cls: 'working', text: 'Part filled' },
  FILLED: { cls: 'executed', text: 'Executed' },
  RECONCILED: { cls: 'executed', text: 'Reconciled' },
  CANCELLED: { cls: 'refused', text: 'Cancelled' },
  REJECTED: {
    cls: 'refused',
    text: 'Rejected',
    title: 'The broker rejected this order. Open the row for its reason.',
  },
  REFUSED: {
    cls: 'refused',
    text: 'Rejected',
    title:
      'A risk check here refused this order before it was sent, so the broker never saw it. ' +
      'Open the row for which check and why.',
  },
  AMBIGUOUS: { cls: 'unknown', text: 'UNKNOWN — check the broker' },
}

/**
 * How an order that WORKED describes itself — ROADMAP 4.13.
 *
 * A verb per state rather than reusing `STATUS.text`, because the pill and
 * the sentence are doing different jobs: the pill is a one-word status read
 * down a column, the sentence is a statement about what happened. "Executed
 * for the Iron Condor strategy" reads; "Executed Executed" does not.
 *
 * A state missing here falls back to "Placed", which is true of anything
 * that reached the broker at all.
 */
const VERB: Record<string, string> = {
  SENT: 'Sent',
  ACKED: 'Working at the exchange',
  PARTIAL: 'Part filled',
  FILLED: 'Executed',
  RECONCILED: 'Executed',
  CANCELLED: 'Cancelled',
}


/**
 * PENDING, then the ORDER BOOK — the mockup's split, on our data.
 *
 * "Pending" is every order that may still be working at the exchange, which
 * is the set a trader can still act on. Everything else is history, and
 * history belongs in a table that can be scanned by time rather than in
 * cards.
 *
 * The mockup's pending section also lists armed guardian RULES. We have no
 * guardian (Phase 7), so that half is deliberately absent rather than
 * stubbed — a "rules" heading with nothing under it reads as a broken
 * feature rather than an unbuilt one.
 */
function OrdersBody({
  orders,
  rows: todays,
}: {
  orders: TrackedIntent[]
  /** Today's rows, one per leg — counted by the badge above. */
  rows: readonly { key: string; at: number; leg: TrackedIntent['legs'][number]; intent: TrackedIntent }[]
}) {
  /*
   * ── WITHDRAWING A RESTING ORDER — 4.17 ──────────────────────────────────
   *
   * The POST writes a REQUEST; the engine's next sweep is what reaches the
   * broker. So this does NOT mark the row cancelled — the state changes when
   * the order is actually gone, and the drawer learns that the same way it
   * learns about a fill. Showing it cancelled here would be the browser
   * claiming something only the broker can confirm.
   *
   * One at a time, like the exit path: a second press while one is in flight
   * is dropped rather than queued.
   */
  const [cancelling, setCancelling] = useState<string | null>(null)

  const cancelOrder = useCallback(
    async (orderRowId: string, label: string): Promise<void> => {
      if (cancelling !== null) return
      setCancelling(orderRowId)
      try {
        await api.post(`/trades/orders/${orderRowId}/cancel`, {})
        notifyOk(
          'Cancel sent',
          `${label} — it leaves the exchange within a few seconds.`,
        )
      } catch (e: unknown) {
        notifyFail(
          'The order was not cancelled',
          e instanceof Error ? e.message : undefined,
        )
      } finally {
        setCancelling(null)
      }
    },
    [cancelling],
  )

  /*
   * ── FILL AT MARKET — ROADMAP 5.14 ───────────────────────────────────────
   *
   * The other door out of a resting order, and the one a trader actually
   * wants. CANCEL takes the order back; this moves it to where the market
   * IS, so it fills. Same incident behind both: on 2026-10-26 a closing
   * marketable limit rested nine minutes while the strike ran 92 -> 139, and
   * the only available action was to withdraw it and start again.
   *
   * ONE CLICK, ONE REPRICE. Deliberately not a toggle: every press re-prices
   * to the touch at that moment, which is what "manual" means here. The
   * automatic version is `autoChase`, which an admin turns on per trader and
   * which does the same thing on a 500 ms timer.
   *
   * NOT SHOWN FOR A STOP OR A TARGET. Protection never appears in this list —
   * it is placed by the protection adapter rather than through an order
   * intent — so there is nothing to filter here. The server refuses one
   * anyway, and `repriceStore` filters again, because "it cannot get here"
   * is a weaker guarantee than "it is refused if it does".
   *
   * It does NOT mark the row re-priced. The engine owns order state, and the
   * new price arrives the same way a fill does.
   */
  const [repricing, setRepricing] = useState<string | null>(null)

  const repriceOrder = useCallback(
    async (orderRowId: string, label: string): Promise<void> => {
      if (repricing !== null) return
      setRepricing(orderRowId)
      try {
        await api.post(`/trades/orders/${orderRowId}/reprice`, {})
        notifyOk(
          'Re-pricing to market',
          `${label} — moved to the current touch.`,
        )
      } catch (e: unknown) {
        notifyFail(
          'The order was not re-priced',
          e instanceof Error ? e.message : undefined,
        )
      } finally {
        setRepricing(null)
      }
    },
    [repricing],
  )

  /*
   * ONE open at a time, and collapsed by default.
   *
   * Five refusals in a row each printed two lines of explanation, which
   * pushed the executed orders off the screen — the book became a wall of
   * the same sentence repeated. The STATUS pill already says a row failed;
   * the reason is what you ask for, not what you are shown.
   */
  const [openRow, setOpenRow] = useState<string | null>(null)
  const cancelAppLimit = useCallback(
    async (intentId: string) => {
      if (cancelling) return
      setCancelling(intentId)
      try {
        const result = await api.post<{ item?: TrackedIntent }>(
          `/trades/intents/${intentId}/cancel-limit`,
          {},
        )
        if (result.item)
          orderStore.hydrate([result.item], orderStore.getVersion())
        notifyOk('App limit cancelled')
      } catch (error) {
        notifyFail(
          'Limit was not cancelled',
          error instanceof Error ? error.message : undefined,
        )
      } finally {
        setCancelling(null)
      }
    },
    [cancelling],
  )
  /*
   * Pending includes app conditions before dispatch and working broker legs.
   *
   * It was filtering on the INTENT's state, which `intentStateFrom` only
   * ever sets to SENT, REJECTED or AMBIGUOUS — an intent is never FILLED.
   * So every order that worked stayed "pending" for the rest of the session,
   * including the marketable limits that filled instantly.
   *
   * The leg is what is actually resting at the exchange, so the leg's state
   * is what decides. A filled, rejected or cancelled leg has nothing left to
   * wait for and belongs only in the book below.
   */
  const pendingLegs = orders.flatMap((o) =>
    o.legs
      .map((leg, i) => ({ key: `${o.clientRequestId}-${i}`, leg, intent: o }))
      .filter(
        ({ leg, intent }) =>
          isWorking(leg.state) || intent.premiumWaiting === true,
      ),
  )
  /* An intent with no legs yet has nothing at the exchange either, but it IS
     in flight and this is the only place it appears. */
  const submitting = orders.filter(
    (o) =>
      o.legs.length === 0 &&
      (['SUBMITTING', 'CREATED', 'GATED'].includes(o.state) ||
        isWorking(o.state)),
  )
  /* Flattened to one row per LEG: the broker's book is per order, and a
     four-leg basket shown as one row hides which leg was rejected. */

  /*
   * ── THE ORDER BOOK IS A SESSION DOCUMENT — 9.29 ─────────────────────────
   *
   * Today's rows only. Yesterday's fills are the journal's business, and
   * leaving them here turned the tab a trader opens to see what JUST
   * happened into a scroll through every order the account has ever sent.
   *
   * Paged at fifty because the list is newest-first and the answer is
   * almost always in the first few — a thousand <tr> nodes re-rendering on
   * every order push is a cost paid for rows nobody scrolls to.
   */
  const [shown, setShown] = useState(ORDER_PAGE)
  const book = useMemo(() => todays.slice(0, shown), [todays, shown])
  const more = todays.length - book.length

  const unconfirmed = orders.filter(
    (o) =>
      o.legs.length === 0 && (o.state === 'AMBIGUOUS' || o.state === 'REFUSED'),
  )

  const nothingPending = pendingLegs.length === 0 && submitting.length === 0

  return (
    <>
      {/*
        ── NO "nothing working" ANYWHERE IN THIS DRAWER ────────────────────
        The whole PENDING ORDERS section is dropped when there is nothing
        pending, heading included, rather than kept with a sentence
        explaining its own emptiness. An absent section says it more
        plainly than a line of prose, and it gives the space back to the
        order book underneath — which is what the trader opened the tab to
        read. Removed on the owner's instruction; it had survived an
        earlier pass because only the other instance was taken out.
      */}
      {!nothingPending && (
        <section>
          <div className="mb-1.5 px-0.5 text-[10px] font-bold tracking-[0.18em] text-muted-foreground">
            PENDING ORDERS
          </div>
          <div className="overflow-hidden rounded-[10px] border border-border">
            <table className="w-full text-[10.5px]">
              <tbody>
                {/*
                  One row per LEG, like the book below. A pending card per
                  INTENT was three lines tall and said less: a four-leg basket
                  collapsed to one row that named a single strike, so the leg
                  still working was not identifiable.
                */}
                {pendingLegs.map(({ key, leg, intent: o }) => (
                  <tr
                    key={key}
                    className="border-b border-border/40 last:border-0"
                    data-testid={`pending-order-${o.clientRequestId}`}
                  >
                    <td className="w-5 py-1.5 pl-2.5">
                      <span
                        className={cn(
                          'tr-dot',
                          leg.state === 'AMBIGUOUS' ? 'text-warning' : 'up',
                        )}
                        style={{ width: 6, height: 6 }}
                      />
                    </td>
                    <td className="num text-muted-foreground">
                      {leg.symbol ?? leg.token}
                    </td>
                    <td className="pr-1">
                      <BrokerCell broker={o.broker} />
                    </td>
                    <td
                      className={cn(
                        'num font-bold',
                        leg.side === 'SELL'
                          ? 'text-destructive'
                          : 'text-success',
                      )}
                    >
                      {leg.side}
                    </td>
                    <td className="num text-right text-muted-foreground">
                      {qtyOf(leg)}
                    </td>
                    <td className="num py-1.5 text-right text-muted-foreground">
                      {o.premiumWaiting
                        ? `WAIT ${leg.side === 'BUY' ? '≤' : '≥'} ₹${o.premiumLimit?.toFixed(2)}`
                        : (STATUS[leg.state]?.text ?? leg.state)}
                    </td>
                    {/*
                        ── THE DOOR OUT OF A RESTING ORDER — 4.17 ──────────
                        An exit is a marketable limit, and a marketable limit
                        stops being marketable when the contract runs. On
                        2026-10-26 one rested nine minutes while the strike
                        went 92 -> 139, visible on this very row, with no way
                        to take it back except the broker's own terminal.
                      */}
                    <td className="py-1.5 pr-2.5 text-right">
                      {leg.orderRowId ? (
                        <button
                          type="button"
                          onClick={() =>
                            void repriceOrder(
                              leg.orderRowId!,
                              leg.symbol ?? String(leg.token),
                            )
                          }
                          disabled={repricing !== null || cancelling !== null}
                          aria-label={`Fill ${leg.symbol ?? leg.token} at market`}
                          data-testid={`reprice-order-${leg.orderRowId}`}
                          title="Move this order to the current market price so it fills"
                          className="mr-1 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-primary transition-colors hover:bg-primary/10 disabled:opacity-40"
                        >
                          {repricing === leg.orderRowId
                            ? '…'
                            : 'FILL AT MARKET'}
                        </button>
                      ) : null}
                      {leg.orderRowId ? (
                        <button
                          type="button"
                          onClick={() =>
                            void cancelOrder(
                              leg.orderRowId!,
                              leg.symbol ?? String(leg.token),
                            )
                          }
                          disabled={cancelling !== null}
                          aria-label={`Cancel ${leg.symbol ?? leg.token}`}
                          data-testid={`cancel-order-${leg.orderRowId}`}
                          title="Withdraw this order from the exchange"
                          className="rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                        >
                          {cancelling === leg.orderRowId ? '…' : 'CANCEL'}
                        </button>
                      ) : o.premiumWaiting && o.intentId ? (
                        <button
                          type="button"
                          disabled={cancelling !== null}
                          onClick={() => {
                            void cancelAppLimit(o.intentId!)
                          }}
                          aria-label={`Cancel app limit ${leg.symbol ?? leg.token}`}
                          title="Cancel this entire limit before it triggers"
                          className="rounded px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                        >
                          {cancelling === o.intentId ? '…' : 'CANCEL'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {/* An intent with no legs yet is still working, and saying so
                    beats dropping it from the only place it appears. */}
                {submitting.map((o) => (
                  <tr
                    key={o.clientRequestId}
                    className="border-b border-border/40 last:border-0"
                    data-testid={`pending-order-${o.clientRequestId}`}
                  >
                    <td className="w-5 py-1.5 pl-2.5">
                      <span
                        className="tr-dot up"
                        style={{ width: 6, height: 6 }}
                      />
                    </td>
                    <td className="text-muted-foreground" colSpan={4}>
                      {o.label}
                    </td>
                    <td className="num py-1.5 pr-2.5 text-right text-muted-foreground">
                      {STATUS[o.state]?.text ?? o.state}
                      {o.premiumWaiting && o.intentId && (
                        <button
                          type="button"
                          disabled={cancelling !== null}
                          onClick={() => {
                            void cancelAppLimit(o.intentId!)
                          }}
                          aria-label={`Cancel app limit ${o.label}`}
                          className="ml-2 rounded px-1.5 py-0.5 text-[9px] font-bold text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                        >
                          {cancelling === o.intentId ? '…' : 'CANCEL'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className={cn(nothingPending || 'mt-3')}>
        <div className="mb-2 px-0.5 text-[10px] font-bold tracking-[0.18em] text-muted-foreground">
          ORDER BOOK
        </div>
        {unconfirmed.map((o) => (
          <div
            key={o.clientRequestId}
            data-testid={`unconfirmed-order-${o.clientRequestId}`}
            className="mb-2 rounded-md border border-border px-3 py-2 text-[11px]"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{o.label}</span>
              <span
                className={
                  o.state === 'AMBIGUOUS' ? 'text-warning' : 'text-destructive'
                }
              >
                {STATUS[o.state]?.text}
              </span>
            </div>
            <p className="mt-1 text-muted-foreground">
              {o.message ?? 'Check the order status before placing it again.'}
            </p>
          </div>
        ))}
        {todays.length === 0 && unconfirmed.length === 0 ? (
          <div className="px-1 py-3 text-center text-[11px] text-muted-foreground">
            No orders this session.
          </div>
        ) : todays.length === 0 ? null : (
          <div className="overflow-hidden rounded-[10px] border border-border">
            <table className="w-full text-[10.5px]">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-[9px] tracking-wider text-muted-foreground">
                  <th className="py-1.5 pl-2.5 text-left font-semibold">
                    TIME
                  </th>
                  <th className="py-1.5 text-left font-semibold">SYMBOL</th>
                  {/* The panel has always been user-scoped rather than
                      account-scoped. Until this column existed, a trader with
                      two brokers had no way to tell which order went where. */}
                  <th className="py-1.5 text-left font-semibold">BROKER</th>
                  <th className="py-1.5 text-left font-semibold">SIDE</th>
                  <th className="py-1.5 text-right font-semibold">QTY</th>
                  <th className="py-1.5 text-right font-semibold">PRICE</th>
                  <th className="py-1.5 pr-2.5 text-right font-semibold">
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody>
                {book.map(({ key, at, leg, intent }) => {
                  /* The LEG's state, not the intent's. A four-leg basket can
                     have one leg filled and one rejected, and one pill for
                     both would be wrong for at least one of them. */
                  const st = STATUS[leg.state] ?? {
                    cls: 'working',
                    text: leg.state,
                  }
                  /*
                   * A failure needs its reason ON the row. The gate name is
                   * what gets quoted in a support question, and a self-trade
                   * cancellation needs its own sentence because the remedy is
                   * in another account entirely ([B21], ROADMAP 5.14).
                   */
                  const selfTrade = isSelfTradeCancel(intent.message)
                  /*
                   * ── ASK THE LEG, BECAUSE THIS ROW IS A LEG ──────────────
                   *
                   * This asked the INTENT, and the two do not agree. Seen in
                   * production on 2026-10-26: five orders died on an expired
                   * token, the reconciler found them absent from seven
                   * order-book reads and settled each LEG as REJECTED with
                   * the broker's own sentence — while the INTENT stayed
                   * AMBIGUOUS, because nothing walks that conclusion back up.
                   *
                   * So the badge beside this read "Rejected" and the row was
                   * not clickable: no cursor, no handler, no detail. The
                   * reason was in the database and unreachable from the one
                   * screen that exists to show it.
                   *
                   * `leg.state` already falls back to the intent's when no
                   * broker order exists (see `orderHistory.service`), so a
                   * gate refusal before dispatch still reads as failed here.
                   */
                  const failed =
                    leg.state === 'REFUSED' ||
                    leg.state === 'REJECTED' ||
                    selfTrade
                  /*
                   * Three sources, because a failure has three origins and
                   * only one of them is ours.
                   *
                   * `message` and `refusedGate` are OUR refusal — a gate said
                   * no before the order left. But an order the BROKER rejects
                   * passes every gate, so both are null, and the row expanded
                   * onto nothing: "click to see why" answering with silence
                   * on exactly the rejections a trader most needs explained.
                   *
                   * The broker's own words are already on the wire, per leg,
                   * because a two-leg order can be refused for a different
                   * reason on each. Deduplicated so the common case — one
                   * sentence repeated across legs — reads as one sentence.
                   */
                  const fromBroker = [
                    ...new Set(
                      intent.legs
                        .map((leg) => leg.reason)
                        .filter((r): r is string => Boolean(r)),
                    ),
                  ].join(' · ')

                  /*
                   * THIS LEG'S OWN REASON FIRST. A basket can be refused for
                   * a different reason on each leg — the wire type says so —
                   * and `fromBroker` joins them all with a separator, so
                   * every expanded row in a four-leg basket showed the same
                   * merged sentence. The intent-level message stays as the
                   * fallback: a gate refusal happens before any leg exists.
                   */
                  const why = selfTrade
                    ? SELF_TRADE_HELP
                    : (leg.reason ??
                      intent.message ??
                      (intent.refusedGate
                        ? RMS_GATE_REASON[intent.refusedGate]
                        : null) ??
                      (fromBroker || null))
                  /*
                   * ── AN ORDER THAT WORKED HAS A STORY TOO ───────────────
                   *
                   * Only refusals were expandable, so the book answered "why
                   * did this fail" and had nothing to say about the forty
                   * rows that succeeded. On a day of several strategies the
                   * question a trader actually asks of an executed row is
                   * "which basket was this FOR" — the leg says NIFTY 22200
                   * CE and four strategies could have sent it.
                   *
                   * Built here rather than on the wire: every part of it is
                   * already on the row, and a sentence assembled in the
                   * browser cannot disagree with the columns beside it.
                   */
                  const placedAt = new Date(at).toLocaleTimeString('en-GB', {
                    hour12: false,
                  })
                  /*
                   * CLOSING ORDERS GET THEIR OWN SENTENCE. "Executed from the
                   * option chain" is wrong for a square-off in a way a trader
                   * notices at once: the one thing they know about that row is
                   * that they pressed EXIT. Reported 2026-11-01 against legs
                   * that closed a strategy.
                   */
                  const closing =
                    intent.kind === 'EXIT' || intent.kind === 'SQUARE_OFF'
                  const what = closing
                    ? `to close ${intent.groupName ? `the ${intent.groupName}` : 'a position'}`
                    : intent.groupName
                      ? `for the ${intent.groupName} strategy`
                      : 'from the option chain'
                  const story = failed
                    ? null
                    : `${VERB[leg.state] ?? 'Placed'} ${what} at ${placedAt}` +
                      (intent.broker ? ` on ${intent.broker.label}` : '') +
                      (leg.filledQty > 0 && leg.avgPrice !== null
                        ? `. ${leg.filledQty.toLocaleString('en-IN')} filled at ${leg.avgPrice.toFixed(2)}.`
                        : '.')
                  const expandable = failed
                    ? Boolean(why || intent.refusedGate)
                    : Boolean(story)

                  return (
                    <Fragment key={key}>
                      <tr
                        className={cn(
                          'border-b border-border/40 transition-colors',
                          expandable && 'cursor-pointer hover:bg-accent/40',
                          !expandable && 'hover:bg-accent/30',
                        )}
                        data-testid={`order-${intent.clientRequestId}`}
                        onClick={
                          expandable
                            ? () => setOpenRow(openRow === key ? null : key)
                            : undefined
                        }
                        title={
                          expandable
                            ? failed
                              ? 'Click to see why'
                              : 'Click to see what this was for'
                            : undefined
                        }
                      >
                        <td className="num py-1.5 pl-2.5 text-muted-foreground">
                          {new Date(at).toLocaleTimeString('en-GB', {
                            hour12: false,
                          })}
                        </td>
                        <td className="num text-muted-foreground">
                          {/* The token is the honest fallback: better a number
                            that identifies the contract than a blank. */}
                          {leg.symbol ?? leg.token}
                        </td>
                        <td className="pr-1">
                          <BrokerCell broker={intent.broker} />
                        </td>
                        <td
                          className={cn(
                            'num font-bold',
                            leg.side === 'SELL'
                              ? 'text-destructive'
                              : 'text-success',
                          )}
                        >
                          {leg.side}
                        </td>
                        <td
                          className="num whitespace-nowrap text-right text-muted-foreground"
                          title={qtyTitle(leg)}
                        >
                          {qtyOf(leg)}
                        </td>
                        {/*
                          ── 9.31. FILLED AT, ELSE RESTING AT ─────────────
                          This printed `avgPrice` alone, which is the AVERAGE
                          FILL and is null until something fills — so a
                          working limit, the one row where the price IS the
                          point, showed a dash. A trader who set a sell at
                          500 saw "Working" with nothing to say what it was
                          working at.

                          A fill wins when there is one, because what a leg
                          actually traded at outranks what was asked. Until
                          then the limit shows, greyed and tagged, so the two
                          are never read as the same number. A MARKET order
                          still shows a dash, correctly — it has no price.
                        */}
                        <td className="num text-right">
                          {leg.avgPrice !== null ? (
                            leg.avgPrice.toFixed(2)
                          ) : leg.limitPrice != null ? (
                            <span
                              className="text-muted-foreground"
                              title={`Resting at ${leg.limitPrice.toFixed(2)}. Nothing has filled yet.`}
                            >
                              {leg.limitPrice.toFixed(2)}
                              <span className="ml-0.5 text-[8px] opacity-70">LMT</span>
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-1.5 pr-2.5 text-right">
                          <span
                            className={cn(
                              'tr-status !px-1.5 !py-0.5 text-[8px]',
                              st.cls,
                            )}
                            data-testid="order-state"
                            title={st.title}
                          >
                            {st.text}
                          </span>
                        </td>
                      </tr>
                      {openRow === key && expandable && (
                        <tr
                          data-testid={
                            selfTrade ? 'order-self-trade' : undefined
                          }
                        >
                          <td colSpan={7} className="px-2.5 pb-1.5">
                            {failed ? (
                              <span className="tr-reason !mt-0">
                                {intent.refusedGate && (
                                  <span
                                    className="tr-code num"
                                    data-testid="order-gate"
                                  >
                                    {intent.refusedGate}
                                  </span>
                                )}
                                <span>{why}</span>
                              </span>
                            ) : (
                              /* Muted, not the refusal's alarm colours. This
                                 is confirmation, and it must not read as a
                                 problem at a glance down the column. */
                              <span
                                className="block pt-0.5 text-[10px] leading-[1.5] text-muted-foreground"
                                data-testid="order-story"
                              >
                                {story}
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
            {/*
              Inside the bordered card rather than below it, so the control
              reads as the bottom of THIS list and not as a page action.
              It says what it will do and how much is left, because "Load
              more" alone gives no sense of whether one press finishes it.
            */}
            {more > 0 ? (
              <button
                type="button"
                onClick={() => setShown((n) => n + ORDER_PAGE)}
                data-testid="orders-load-more"
                className="flex w-full items-center justify-center gap-1.5 border-t border-border bg-secondary/30 py-2 text-[10.5px] font-medium text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
              >
                <ChevronDown size={13} aria-hidden="true" />
                Load {Math.min(ORDER_PAGE, more)} more
                <span className="text-muted-foreground/70">
                  ({more} remaining today)
                </span>
              </button>
            ) : null}
          </div>
        )}
      </section>
    </>
  )
}

/**
 * Which account an order went to.
 *
 * An em dash when the intent never got one — it was refused before a
 * connection was resolved — rather than a blank, which reads as a column
 * that failed to render.
 */
function BrokerCell({ broker }: { broker: TrackedIntent['broker'] }) {
  if (!broker) return <span className="text-muted-foreground">—</span>
  return (
    <span
      className="block max-w-[92px] truncate rounded bg-secondary px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground"
      title={`${broker.label} · ${broker.mode}`}
      data-testid="order-broker"
    >
      {broker.label}
    </span>
  )
}

const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="px-2 py-8 text-center text-[12px] text-muted-foreground">
    {children}
  </div>
)
